// ============================================================================
// Action Generator Helpers
// ============================================================================
// Type guards and utility functions to reduce duplication in action generators.
// ============================================================================

import {
  getObjectProperties,
  type ObjectCategoryValue,
  type ObjectTypeValue,
  toObjectType,
  type WorldObjectSnapshot,
} from '@dreamgame/shared';
import type { ContextMenuTarget } from './contextMenu';

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Narrows a ContextMenuTarget to one with a valid object.
 */
export interface ObjectTarget extends ContextMenuTarget {
  readonly type: 'object';
  readonly object: WorldObjectSnapshot;
}

/**
 * Type guard that checks if the target is an object with a valid WorldObjectSnapshot.
 */
export const isObjectTarget = (target: ContextMenuTarget): target is ObjectTarget => {
  return target.type === 'object' && target.object !== undefined;
};

// ============================================================================
// Object Type Helpers
// ============================================================================

/**
 * Gets the typed ObjectTypeValue from a target's object.
 * Uses toObjectType validator instead of unsafe cast.
 */
export const getTargetObjectType = ({ target }: { target: ObjectTarget }): ObjectTypeValue => {
  return toObjectType(target.object.objectType);
};

/**
 * Gets object properties for a target's object.
 * Combines the common pattern of getting object type and then properties.
 */
export const getTargetObjectProperties = ({ target }: { target: ObjectTarget }) => {
  const objectType = getTargetObjectType({ target });
  return getObjectProperties({ objectType });
};

/**
 * Checks if the target object's category matches the expected category.
 */
export const targetHasCategory = ({
  target,
  category,
}: {
  target: ObjectTarget;
  category: ObjectCategoryValue;
}): boolean => {
  const props = getTargetObjectProperties({ target });
  return props.category === category;
};

/**
 * Checks if the target object has a specific property set to true.
 */
export const targetHasProperty = ({
  target,
  property,
}: {
  target: ObjectTarget;
  property: 'harvestable' | 'destructible' | 'buildable' | 'blockMovement' | 'flammable';
}): boolean => {
  const props = getTargetObjectProperties({ target });
  return props[property];
};
